// A returning account's way back in.
//
// The runtime writes a one-bit marker cookie when an account session is born on
// this host (`/__sf/redeem`), spends it at logout and at every silent start, and
// names it in the collab manifest. This module only reads it. With the marker,
// a page whose first ticket would make this browser a visitor takes one
// top-level trip through the account start with `silent=1`: the sign-in host
// either hands back an account session or sends the browser straight back, and
// either way the page loads again. Without the marker, or on an engine whose
// manifest names none, nothing here ever navigates. The threat model sits
// beside the marker's definition in runtime/engine/runtime/access-rules.php.

import type { CollabManifest } from "@spacefast/common/contracts/collab-manifest";

/**
 * This tab's last attempt. The runtime spending the marker is what stops a
 * loop; this stops one too where a marker outlives its spending, and it is what
 * lets the page a trip comes back to know the trip was its own.
 */
export const RENEWAL_ATTEMPT_KEY = "spacefast:account-renewal";
const RENEWAL_COOLDOWN_MS = 2 * 60_000;
/**
 * The trip replaces the page, so it is only taken while the page is new: past
 * this, whatever the reader has started is worth more than the renewal.
 */
const RENEWAL_WINDOW_MS = 10_000;
/**
 * The access flow's own params, never part of a page a trip returns to, and
 * `__` is a share-link secret. Mirrors STATTIC_ACCESS_FLOW_PARAMS in
 * access-rules.php.
 */
const FLOW_PARAMS = new Set(["sf_access", "sf_access_return", "sf_access_as", "__"]);

/** What this host offers a returning account: where to go, and the marker to look for. */
export type AccountRenewal = { accountUrl: string; cookie: string };

/** Null on an unclaimed Space, and on an engine that cannot renew an account. */
export function accountRenewalOf(
  manifest: Pick<CollabManifest, "accountUrl" | "returningAccountCookie">,
): AccountRenewal | null {
  const { accountUrl, returningAccountCookie: cookie } = manifest;
  return accountUrl && cookie ? { accountUrl, cookie } : null;
}

export type RenewalEnvironment = {
  cookie(): string;
  now(): number;
  /** Milliseconds since this page's navigation started. */
  sinceNavigation(): number;
  /** Only a top-level page takes the trip: in a frame it would carry the sign-in host into the frame. */
  topLevel(): boolean;
  location: Pick<Location, "origin" | "pathname" | "search" | "hash">;
  /** This tab's sessionStorage, which survives the round trip. Null where storage is blocked. */
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  navigate(url: string): void;
  replaceUrl(url: string): void;
};

function sessionStorageOrNull(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Blocked storage throws on access, not on use.
    return null;
  }
}

function browserRenewalEnvironment(): RenewalEnvironment {
  return {
    cookie: () => globalThis.document?.cookie ?? "",
    now: () => Date.now(),
    sinceNavigation: () => globalThis.performance?.now() ?? Number.POSITIVE_INFINITY,
    topLevel: () => globalThis.top === globalThis.self,
    location: globalThis.location,
    storage: sessionStorageOrNull(),
    // The trip comes back to this same page, so it takes this page's place in
    // history: Back leaves the page instead of landing on its pre-trip copy.
    navigate: (url) => globalThis.location.replace(url),
    replaceUrl: (url) => globalThis.history.replaceState(globalThis.history.state, "", url),
  };
}

/** Whether this host's marker says an account has signed in here in this browser. */
export function returningAccount(cookie: string, name: string): boolean {
  return cookie.split(";").some((pair) => {
    const [cookieName, value] = pair.trim().split("=", 2);
    return cookieName === name && value === "1";
  });
}

/** The query minus the flow's own params, every other pair spelled as it was. */
function withoutFlowParams(search: string): string {
  const kept = search
    .replace(/^\?/, "")
    .split("&")
    .filter((pair) => {
      if (pair === "") return false;
      const name = pair.split("=", 1)[0] ?? "";
      try {
        return !FLOW_PARAMS.has(decodeURIComponent(name.replaceAll("+", " ")));
      } catch {
        return true;
      }
    });
  return kept.length > 0 ? `?${kept.join("&")}` : "";
}

/**
 * The account start, told to come back to this exact page. The hash rides the
 * trip's own URL, never a server's: a fragment is not sent, and the browser
 * carries it through every redirect that names none, back onto the page. Null
 * for an account start on any other origin.
 */
export function accountRenewalUrl(
  renewal: AccountRenewal,
  location: Pick<Location, "origin" | "pathname" | "search" | "hash">,
): string | null {
  let start: URL;
  try {
    start = new URL(renewal.accountUrl);
  } catch {
    return null;
  }
  if (start.origin !== location.origin) return null;
  start.searchParams.set("silent", "1");
  start.searchParams.set("return", `${location.pathname}${withoutFlowParams(location.search)}`);
  start.hash = location.hash;
  return start.href;
}

function attemptedRecently(storage: Pick<Storage, "getItem">, now: number): boolean {
  return now - Number(storage.getItem(RENEWAL_ATTEMPT_KEY)) < RENEWAL_COOLDOWN_MS;
}

/** Leaves for the sign-in host when this browser may. True when the page is leaving. */
export function startAccountRenewal(
  renewal: AccountRenewal | null,
  env: RenewalEnvironment = browserRenewalEnvironment(),
): boolean {
  if (
    !renewal ||
    !env.topLevel() ||
    env.sinceNavigation() > RENEWAL_WINDOW_MS ||
    !returningAccount(env.cookie(), renewal.cookie) ||
    !env.storage
  ) {
    return false;
  }
  const url = accountRenewalUrl(renewal, env.location);
  if (!url) return false;
  try {
    if (attemptedRecently(env.storage, env.now())) return false;
    env.storage.setItem(RENEWAL_ATTEMPT_KEY, String(env.now()));
  } catch {
    // Nowhere to remember the attempt means no attempt.
    return false;
  }
  env.navigate(url);
  return true;
}

/**
 * The page a renewal came back to empty-handed: take the sign-in host's note
 * off the address bar, so the page reads as it did before the trip.
 */
export function settleAccountRenewal(env: RenewalEnvironment = browserRenewalEnvironment()): void {
  const { location, storage } = env;
  const clean = withoutFlowParams(location.search);
  if (!storage || clean === location.search) return;
  try {
    if (!attemptedRecently(storage, env.now())) return;
  } catch {
    return;
  }
  env.replaceUrl(`${location.pathname}${clean}${location.hash}`);
}
